import React, { useState, useRef, useEffect } from "react";
import "./Chatbot.css";
import latestNews from "../data/latestNews";

const newsBlock = latestNews
  .map(
    (n, i) =>
      `${i + 1}. [${n.date_fr} / ${n.date_en}]\n   FR: ${n.title_fr} — ${n.summary_fr}\n   EN: ${n.title_en} — ${n.summary_en}`,
  )
  .join("\n\n");

const SYSTEM_PROMPT = `You are the virtual assistant of SACEM Industries. Be professional, concise, and helpful. If a question goes beyond your knowledge of SACEM, direct the user to the Contact page on the website.

## LANGUAGE RULE — CRITICAL
- Detect the language of EACH user message.
- ALWAYS reply in the EXACT same language the user used (French if they write in French, English if they write in English).
- NEVER mix languages in a single response.
- This rule overrides everything else and applies to every single message without exception.

## SACEM Industries — Overview
- Tunisian leader in the manufacturing and distribution of electrical transformers
- Founded in 1966, headquarters in Menzel Bourguiba-Tinja, Tunisia
- 6-hectare factory, 18,000 m² of covered area
- 450 qualified employees
- Over 10,000 transformers manufactured
- Production capacity: 2,000 MVA/year
- Export to more than 30 countries

## SACEM Group
- **SACEM Industries**: Manufacturing of single-phase, three-phase and special transformers
- **SACEM Service**: Reactive after-sales service, maintenance and repair of transformers
- **SACEM Training**: First African training center for transformers (50+ years of expertise, programs compliant with international standards)
- **SACEM Power**: Manufacturing of MV/LV switchgear and cells

## Products

### Three-Phase Distribution Transformers (≤24 kV and ≤36 kV)
- Oil-immersed, hermetically sealed or breathing filling
- Natural ONAN cooling
- Indoor/outdoor installation
- Color: light gray RAL 7035
- IEC 60076 compliant | Approved by CESI, KEMA, VEIKI

### Three-Phase Power Transformers
- Oil-immersed, ONAN/ONAF cooling
- Indoor/outdoor installation
- IEC 60076 compliant | High efficiency, long-term reliability

### Single-Phase Transformers
- Oil-immersed, ONAN cooling
- C5H anti-corrosion protection
- Color: RAL 7035 (other colors on request)
- IEC 60076 compliant

### Special Transformers (custom-made)
- Earthing transformers, autotransformers, isolation transformers
- Designed for specific applications by an experienced team

### Power Systems (SACEM Power)
- MV/LV switchgear and cells
- Transformer protection cubicles
- Active filters, capacitor banks
- AIS switchgear

## Services
- **After-Sales & Repair**: Preventive and corrective maintenance, rapid response
- **Training**: Certified programs on transformers, first African center of its kind
- **Export**: Presence in 30+ countries

## Certifications
- ISO 9001 (Quality Management)
- ISO 14001 (Environmental Management)
- ISO 45001 (Occupational Health and Safety)
- Products approved by: CESI, KEMA, VEIKI

## Addresses
### Head Office
- FR: Imm. Mak Crown, 3ème étage, Rue du Lac Tanganyika, Les Berges du Lac, 1053 Tunis, Tunisie
- EN: Building Mak Crown, 3rd floor, Road Lac Tanganyika, Les Berges du Lac, 1053 Tunis, Tunisia

### Factory
- FR: Zone industrielle Sidi Rézig, Tinja - 7032 Menzel Bourguiba, Tunisie
- EN: Industrial Zone Sidi Rezig, Tinja - 7032 Menzel Bourguiba, Tunisia

## Contact & Site Pages
- Contact page: online form + direct contacts of our sales representatives
- FAQ: frequently asked questions about products and services
- News page: full list of SACEM Industries news (FR: /actualites | EN: /en/news)
- Job offers available on the Careers page

## Latest News (last 3 articles)
${newsBlock}

When asked about news or recent events, share the 1–2 most relevant articles above with a short summary, then suggest visiting the News page for more.

## Sales Representatives

### M.Nizar HBAILI — Area Sales Manager Africa
- Phone: +(216) 28 505 116
- WhatsApp: +(225) 07 47 12 76 86
- Email: Nizar.HBAILI@sacemgroup.com
- Region: Sub-Saharan Africa, West and Central Africa

### M.Oussama KAABI — Area Sales Manager Algeria-Africa
- Phone: +(216) 29 493 151
- WhatsApp: +(216) 29 493 151
- Email: Oussama.KAABI@sacemgroup.com
- Region: Algeria and North Africa (excluding Tunisia and Libya)

### M.Abdlhamid NASSR — Production & Sales Manager Libya
- Phone: +(216) 29 922 626
- WhatsApp: +(216) 29 922 626
- Email: Abdulhamid.NASSR@sacemgroup.com
- Region: Libya

### Mme.Rahma ZAALANI — Local Sales Manager Tunisia
- Phone: +(216) 29 591 207
- WhatsApp: +(216) 29 591 207
- Email: Rahma.ZAALANI@sacemgroup.com
- Region: Tunisia and local market

## Instructions for directing users to a sales representative
- When a user asks for a quote, commercial information, a partnership, or expresses a purchasing need:
  1. If their region/country is NOT mentioned: ask ONE short question to find out their country or region. Do not display any contact at this stage.
  2. Once the region is known: recommend ONLY the corresponding sales representative (name, title, phone, email). Nothing more.
- Never list all sales representatives at once. Always target one only.
- If the region does not match any specific zone, recommend Rahma ZAALANI (Tunisia) and mention the "Nous Contacter" page for more options.
- Full profiles with photos are on the "Nous Contacter" page of the website.

Be clear, brief, and professional. Get straight to the point.`;

const CATEGORIES = [
  "Transformers",
  "Tableau MT-BT",
  "SAV & Réparation",
  "Training",
];

const CATEGORY_RESPONSES = {
  Transformers:
    "Voici nos gammes de transformateurs :\n\n• Transformateurs de distribution triphasés ≤ 24 kV\n• Transformateurs de distribution triphasés ≤ 36 kV\n• Transformateurs de puissance triphasés\n• Transformateurs monophasés\n• Transformateurs spéciaux (sur mesure)\n\nSur quelle gamme souhaitez-vous plus d'informations ?",
  "Tableau MT-BT":
    "Voici les produits SACEM Power (MT/BT) :\n\n• Tableau-AIS\n• coffret de protection de transformateur\n• SEMS\n• Filtre actif anti-harmonique\n• Batterie de Condensateurs\n\nQuel produit vous intéresse ?",
  "SAV & Réparation":
    "Nos services SAV & Réparation (SACEM Service) :\n\n• Maintenance préventive\n• Maintenance corrective\n• Réparation de transformateurs\n• Interventions rapides sur site\n\nComment pouvons-nous vous aider ?",
  Training:
    "Nos programmes de formation (SACEM Training) :\n\n• Formation transformateurs MT/BT\n• Maintenance & exploitation\n• Sécurité électrique\n• Programmes certifiés conformes aux normes internationales\n\nQuel programme vous intéresse ?",
};

const GROQ_API_URL = "https://api.groq.com/openai/v1/chat/completions";

const GROQ_MODEL = "openai/gpt-oss-120b";

const WELCOME_MESSAGE = {
  role: "assistant",
  content:
    "Bonjour ! Je suis l'assistant virtuel de SACEM Industries. Comment puis-je vous aider ?\n\nHello! I'm SACEM Industries' virtual assistant. How can I help you?",
};

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const doSend = async (text) => {
    if (!text || isLoading) return;

    const userMessage = { role: "user", content: text };
    const newMessages = [...messages, userMessage];
    setInput("");
    inputRef.current?.focus();

    if (CATEGORY_RESPONSES[text]) {
      setMessages([
        ...newMessages,
        { role: "assistant", content: CATEGORY_RESPONSES[text] },
      ]);
      return;
    }

    setMessages(newMessages);
    setIsLoading(true);

    try {
      const response = await fetch("/api/chat.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          messages: [
            { role: "system", content: SYSTEM_PROMPT },
            ...newMessages,
          ],
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const reply = data.choices?.[0]?.message?.content ?? "";
      setMessages([...newMessages, { role: "assistant", content: reply }]);
    } catch {
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content:
            "Désolé, une erreur est survenue. Veuillez réessayer.\nSorry, an error occurred. Please try again.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = () => doSend(input.trim());

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="cb-container">
      {isOpen && (
        <div className="cb-window" role="dialog" aria-label="Assistant SACEM">
          <div className="cb-header">
            <div className="cb-header-info">
              <div className="cb-avatar" aria-hidden="true">
                S
              </div>
              <div className="cb-header-text">
                <span className="cb-title">Assistant SACEM</span>
                <span className="cb-status">
                  <span className="cb-dot" aria-hidden="true" />
                  En ligne
                </span>
              </div>
            </div>
            <button
              className="cb-close"
              onClick={handleToggle}
              aria-label="Fermer le chat"
            >
              ✕
            </button>
          </div>

          <div className="cb-messages" role="log" aria-live="polite">
            <div className="cb-msg cb-msg--assistant">
              <p className="cb-msg-text">{WELCOME_MESSAGE.content}</p>
            </div>

            {messages.length === 0 && (
              <div className="cb-categories">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    className="cb-category-btn"
                    onClick={() => doSend(cat)}
                    disabled={isLoading}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`cb-msg cb-msg--${msg.role}`}>
                <p className="cb-msg-text">{msg.content}</p>
              </div>
            ))}

            {isLoading && (
              <div
                className="cb-msg cb-msg--assistant cb-typing"
                aria-label="En train d'écrire"
              >
                <span />
                <span />
                <span />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="cb-input-area">
            <textarea
              ref={inputRef}
              className="cb-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message…"
              rows={1}
              disabled={isLoading}
              aria-label="Saisir un message"
            />
            <button
              className="cb-send"
              onClick={sendMessage}
              disabled={isLoading || !input.trim()}
              aria-label="Envoyer"
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="18"
                height="18"
              >
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <button
        className={`cb-toggle ${isOpen ? "cb-toggle--open" : ""}`}
        onClick={handleToggle}
        aria-label={isOpen ? "Fermer le chat" : "Ouvrir le chat"}
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
          </svg>
        )}
      </button>
    </div>
  );
}
